import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, map, Observable, of } from 'rxjs';
import { RunnerPairingService, type PairedRunner } from '../../services/runner-pairing.service';
import { ExecutionJobService, type RunTarget } from '../../services/execution-job.service';

type RunnerLoadState = 'loading' | 'loaded' | 'failed';

/** Ein gespeichertes Ziel, etwa das eines Zeitplans (NEX-1761). */
export interface RunnerTargetInitial {
  runnerId: string | null;
  labels: string[];
}

/** Werte der Auswahlliste: Rechner tragen ihre Id, Anforderungen dieses Praefix vor dem Etikett. */
const LABEL_PREFIX = 'label:';

/**
 * Auswahl, wo ein automatisierter Lauf laeuft: ein bestimmter Rechner oder eine Anforderung
 * (NEX-1257, NEX-1513). Herausgeloest aus dem Ausfuehren-Dialog, damit auch der neue Durchlauf
 * eines Runner-Laufs dieselbe Liste zeigt (NEX-1601).
 *
 * <p><b>Ohne Design-Entwurf.</b> Das Bundle kennt die Zeile "Laeuft auf" nicht; die Begruendung
 * steht am Ausfuehren-Dialog (`run-test-case-dialog`). Die Klassen tragen deshalb noch das Praefix des
 * Dialogs, aus dem sie stammen.
 *
 * <p>`targetChange` meldet `null`, solange kein Ziel feststeht — auch waehrend des (erneuten)
 * Ladens: Bis zur Antwort ist offen, ob der gewaehlte Rechner noch verbunden ist.
 */
@Component({
  selector: 'app-runner-target-select',
  imports: [DatePipe, MatButton, MatIcon, MatProgressSpinner, RouterLink, TranslatePipe],
  templateUrl: './runner-target-select.component.html',
  styleUrl: './runner-target-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "visible() ? null : 'none'" },
})
export class RunnerTargetSelectComponent implements OnInit {
  private readonly runnerPairing = inject(RunnerPairingService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Ausgeblendet bleibt die Komponente am Leben: Liste und Wahl ueberstehen den Wechsel der Art
   * im Ausfuehren-Dialog, ohne neu zu laden.
   */
  readonly visible = input(true);

  /**
   * Gesetzt, waehlt die Komponente nach dem Laden genau dieses Ziel statt des eigenen Rechners.
   * Fehlt der Rechner in der Liste, bleibt die Wahl leer: Ein Rueckfall auf den eigenen Rechner
   * wechselte das gespeicherte Ziel still. Mehrere Etiketten bietet die Liste nicht an, sie
   * bleiben deshalb unvorbelegt.
   */
  readonly initial = input<RunnerTargetInitial | null>(null);

  /**
   * Gesetzt, waehlt die Liste einen Rechner fuer eine Aufnahme in diesem Projekt (NEX-1719): ohne
   * Anforderung, und Rechner ohne Faehigkeit RECORDING sind ausgegraut. Woher `canRecord` kommt,
   * weiss nur der Projekt-Endpunkt der Auftraege; die Liste der verbundenen Rechner kennt es nicht.
   */
  readonly recordingProjectId = input<string | null>(null);

  readonly recordingMode = computed(() => this.recordingProjectId() !== null);

  /** Ids der Rechner, die aufnehmen koennen; nur im Aufnahme-Modus gefuellt. */
  private readonly recordable = signal<ReadonlySet<string>>(new Set());

  readonly targetChange = output<RunTarget | null>();
  /** Der Link zur Kontoseite: Ein umgebender Dialog muss sich dabei schliessen. */
  readonly connect = output<void>();

  /**
   * Alle verbundenen Rechner des Mandanten, auch die gerade ausgeschalteten. Ohne die offline
   * Rechner verschwaende der eigene Laptop still aus der Liste, sobald er aus ist.
   */
  readonly runners = signal<PairedRunner[]>([]);

  /**
   * Bis zur Antwort ist nichts bekannt. Ohne eigenen Zustand stuende hier "Noch kein Rechner
   * verbunden" — auch bei einem Fehler, und das waere schlicht falsch.
   */
  readonly loadState = signal<RunnerLoadState>('loading');

  /**
   * `null` heisst "noch nicht gewaehlt". Einen Auftrag ohne Empfaenger gibt es nicht mehr: Den
   * nahm der Rechner, der zuerst fragte — womoeglich der Laptop einer Kollegin (NEX-1509).
   * Eine Anforderung wird deshalb auch nie vorbelegt, sie ist immer eine bewusste Wahl.
   */
  readonly selectedValue = signal<string | null>(null);

  readonly selectedRunnerId = computed(() => {
    const value = this.selectedValue();
    return value === null || value.startsWith(LABEL_PREFIX) ? null : value;
  });

  readonly selectedLabel = computed(() => {
    const value = this.selectedValue();
    return value?.startsWith(LABEL_PREFIX) ? value.slice(LABEL_PREFIX.length) : null;
  });

  /**
   * Je verschiedenem Etikett der eingeschalteten Rechner ein Eintrag. Etiketten ausgeschalteter
   * Rechner fehlen: Der Server nimmt nur an, was ein verbundener Rechner traegt — der Eintrag
   * fuehrte sicher in die Absage. Kombinationen kann der Server schon, angeboten werden sie noch nicht.
   */
  readonly labelOptions = computed(() => {
    const labels = new Set<string>();
    for (const runner of this.runners()) {
      if (runner.online) {
        [...runner.reportedLabels, ...runner.managedLabels].forEach((label) => labels.add(label));
      }
    }
    const initialLabel = this.initialLabel();
    if (initialLabel !== null) {
      labels.add(initialLabel);
    }
    return [...labels].sort();
  });

  readonly labelPrefix = LABEL_PREFIX;

  /** Ein gespeichertes Etikett steht in der Liste, auch wenn es gerade kein eingeschalteter Rechner traegt. */
  private readonly initialLabel = computed(() => {
    const initial = this.initial();
    return initial?.runnerId === null && initial.labels.length === 1 ? initial.labels[0] : null;
  });

  readonly target = computed<RunTarget | null>(() => {
    if (this.loadState() !== 'loaded') {
      return null;
    }
    const label = this.selectedLabel();
    if (label !== null) {
      return { kind: 'labels', labels: [label] };
    }
    const runner = this.runners().find((each) => each.id === this.selectedRunnerId());
    return runner && !this.cannotRecord(runner)
      ? { kind: 'runner', runnerId: runner.id, runnerName: runner.name }
      : null;
  });

  /*
   * Die Auswahlliste bindet [selected] je Option statt [value] am select: Das select wird
   * gebunden, bevor @for die Optionen anlegt — die Zuweisung verpufft, und die Liste zeigt den
   * ersten Eintrag, waehrend gestartet wird, was hier steht. Der Nutzer laese dann einen anderen
   * Rechner, als der Lauf nimmt.
   */

  /** Erst hier, nicht im Konstruktor: Ob der Aufnahme-Modus gilt, steht erst mit den Inputs fest. */
  ngOnInit(): void {
    this.loadRunners();
  }

  /** Im Aufnahme-Modus nur Rechner, die aufnehmen koennen. */
  cannotRecord(runner: PairedRunner): boolean {
    return this.recordingMode() && !this.recordable().has(runner.id);
  }

  onRunnerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedValue.set(value === '' ? null : value);
    this.targetChange.emit(this.target());
  }

  loadRunners(): void {
    this.loadState.set('loading');
    this.targetChange.emit(null);
    combineLatest([this.runnerPairing.listRunners(), this.loadRecordable()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([runners, recordable]) => {
          this.runners.set(runners);
          this.recordable.set(recordable);
          this.selectedValue.set(this.preselect(runners));
          this.loadState.set('loaded');
          this.targetChange.emit(this.target());
        },
        error: () => this.loadState.set('failed'),
      });
  }

  private loadRecordable(): Observable<ReadonlySet<string>> {
    const projectId = this.recordingProjectId();
    if (projectId === null) {
      return of(new Set<string>());
    }
    // Erst hier geholt: Ohne Aufnahme braucht die Liste den Dienst nicht, ihre Verwender stellen ihn nicht immer bereit.
    return this.injector
      .get(ExecutionJobService)
      .listRecordingRunners(projectId)
      .pipe(map((runners) => new Set(runners.filter((runner) => runner.canRecord).map((runner) => runner.id))));
  }

  private selectable(runner: PairedRunner): boolean {
    return runner.online && !this.cannotRecord(runner);
  }

  private preselect(runners: PairedRunner[]): string | null {
    const initial = this.initial();
    if (initial === null) {
      // Ohne eigenen Rechner bewusst keine Vorbelegung statt des alphabetisch ersten: Der
      // waere womoeglich der Laptop einer Kollegin, und der Lauf oeffnete dort ein Fenster.
      // Ein ausgeschalteter eigener Rechner nimmt nichts an — vorbelegt waere er ein Fehlstart.
      return runners.find((runner) => runner.preferred && this.selectable(runner))?.id ?? null;
    }
    const initialLabel = this.initialLabel();
    if (initialLabel !== null) {
      return LABEL_PREFIX + initialLabel;
    }
    return runners.find((runner) => runner.id === initial.runnerId)?.id ?? null;
  }
}
